import { Moment } from 'moment-timezone';

export const formatPhoneNumberForDisplay = (value: string): string => {
    if (!value) {
        return value;
    }

    const digits = value.replace(/[^\d]/g, '');

    if (digits.length === 10) {
        // 10-digit US number without country code
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
        // 11-digit US number with country code (1 + 10 digits)
        // Format: +1 (XXX) XXX-XXXX
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    } else {
        return value;
    }
};

export const formatPhoneNumberForBackend = (value: string): string => {
    // Remove non-numeric characters.
    return value.replace(/[^\d]/g, '');
};

// Strips the `+1` the patient query returns, which the edit endpoint, the form fields and
// `phoneValidator` all expect gone. Any other prefix stays, as it does on the profile tab.
export const unprefixedPhoneNumber = (phoneNumber: string | null | undefined): string => {
    if (!phoneNumber) {
        return '';
    }

    return phoneNumber.startsWith('+1') ? phoneNumber.slice(2) : phoneNumber;
};

// Formats an entered phone number as E.164, adding the +1 country code to a bare US number.
// An empty entry becomes null.
export const toE164PhoneNumber = (phoneNumber: string): string | null => {
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed.startsWith('+') ? trimmed : `+1${trimmed}`;
};

export const formatInitial = (name: string): string => {
    return name.charAt(0).toUpperCase();
};

// Up-to-two-letter initials from a first and last name.
export const formatInitials = (
    firstName: string | null | undefined,
    lastName: string | null | undefined,
): string =>
    [firstName, lastName]
        .map((part) => (part ?? '').charAt(0))
        .join('')
        .toUpperCase();

// Up-to-two-letter initials from a full name, taken from its first and last words.
export const initialsFromName = (name?: string | null): string | undefined => {
    const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (parts.length === 0) {
        return undefined;
    }
    return formatInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
};

export const formatCents = (cents: number): string => {
    return cents === 0 ? '$0' : '$' + (cents / 100).toFixed(2).toString();
};

export const orgPrefixedName = (
    obj: { name: string; organization: { name: string } },
    showOrgName: boolean,
): string => (showOrgName ? `${obj.organization.name} // ${obj.name}` : obj.name);

export const schedulingConstraintsStr = ({
    minVisitsPerWeek,
    maxVisitsPerWeek,
    startDate,
    endDate,
    duration,
    unitTime,
}: {
    minVisitsPerWeek?: number | null;
    maxVisitsPerWeek?: number | null;
    startDate?: Moment | null;
    endDate?: Moment | null;
    duration?: number | null;
    unitTime?: number | null;
}) => {
    if (
        !minVisitsPerWeek &&
        !maxVisitsPerWeek &&
        !startDate &&
        !endDate &&
        !duration &&
        !unitTime
    ) {
        return 'No constraints';
    }

    const minVisitsPerWeekStr = minVisitsPerWeek ? `at least ${minVisitsPerWeek}` : '';
    const maxVisitsPerWeekStr = maxVisitsPerWeek ? `at most ${maxVisitsPerWeek}` : '';
    const startDateStr = startDate ? `starting ${startDate.format('MM/DD/YYYY')}` : '';
    const endDateStr = endDate ? `ending ${endDate.format('MM/DD/YYYY')}` : '';
    return `${minVisitsPerWeekStr}${
        minVisitsPerWeekStr && maxVisitsPerWeek ? ' and ' : ''
    }${maxVisitsPerWeekStr}${
        unitTime ? ' time(s) every ' + unitTime.toString() + ' week(s) ' : ''
    }${duration ? `for ${duration} weeks ` : ''}${startDateStr}${
        startDateStr && endDateStr ? ' and ' : ''
    }${endDateStr}`.replace(/^\w/, (match) => match.toUpperCase());
};

export const roundNumber = (num: number, maxDecimalPlaces: number): number => {
    return Math.round(num * 10 ** maxDecimalPlaces) / 10 ** maxDecimalPlaces;
};
